import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound, ArrowLeft, Delete, LogIn, ShieldOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const ROLE_LABEL = { admin: "Administrador", director: "Director", manager: "Gerente", finance: "Financeiro", accountant: "Contabilista", purchasing: "Compras", warehouse: "Armazém", sales: "Comercial", cashier: "Caixa", hr: "RH", driver: "Motorista", maintenance: "Manutenção", staff: "Funcionário", kitchen: "Cozinha", waiter: "Empregado de mesa", stock: "Stock" };
const COLORS = ["bg-rose-500", "bg-amber-500", "bg-emerald-500", "bg-sky-500", "bg-violet-500", "bg-fuchsia-500", "bg-teal-500", "bg-orange-500"];
const colorFor = (s = "") => COLORS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";

const ProfileCard = ({ p, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(p)}
    className="group flex flex-col items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25 hover:-translate-y-1 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    data-testid={`profile-card-${p.user_id}`}
  >
    <div className={`relative w-24 h-24 rounded-2xl ${colorFor(p.name)} flex items-center justify-center text-3xl font-bold text-white shadow-xl overflow-hidden`}>
      {p.picture ? <img src={p.picture} alt="" className="w-full h-full object-cover" /> : initials(p.name)}
      {!p.has_pin && <span className="absolute bottom-1 right-1 rounded-full bg-black/60 p-1" title="Sem PIN definido"><Lock className="w-3.5 h-3.5 text-white/80" /></span>}
    </div>
    <div className="text-center">
      <p className="font-semibold text-white leading-tight">{p.name}</p>
      <p className="text-xs text-white/50 mt-0.5">{ROLE_LABEL[p.role] || p.role}{p.is_current ? " · sessão activa" : ""}</p>
    </div>
  </button>
);

const PinPad = ({ profile, onBack, onDone }) => {
  const { pinLogin } = useAuth();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (pin.length !== 4 || busy) return;
    (async () => {
      setBusy(true);
      try {
        await pinLogin(profile.user_id, pin);
        toast.success(`Bem-vindo(a), ${profile.name.split(" ")[0]}!`);
        onDone();
      } catch (e) {
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setPin("");
        toast.error(e.response?.data?.detail || "PIN incorrecto");
      } finally {
        setBusy(false);
      }
    })();
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (/^\d$/.test(e.key)) setPin((p) => (p.length < 4 ? p + e.key : p));
      else if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
      else if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const press = (d) => setPin((p) => (p.length < 4 ? p + d : p));

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-xs mx-auto animate-in fade-in zoom-in-95 duration-200" data-testid="pin-pad">
      <div className={`w-20 h-20 rounded-2xl ${colorFor(profile.name)} flex items-center justify-center text-2xl font-bold text-white shadow-xl overflow-hidden`}>
        {profile.picture ? <img src={profile.picture} alt="" className="w-full h-full object-cover" /> : initials(profile.name)}
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-white" data-testid="pin-profile-name">{profile.name}</p>
        <p className="text-sm text-white/50">Introduza o seu PIN de 4 dígitos</p>
      </div>
      <div className={`flex gap-4 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`} data-testid="pin-dots" data-length={pin.length}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${i < pin.length ? "bg-primary border-primary scale-110" : "border-white/30"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 w-full">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" onClick={() => press(d)} disabled={busy} className="h-16 rounded-xl bg-white/[0.06] hover:bg-white/[0.14] active:scale-95 text-2xl font-semibold text-white transition-all" data-testid={`pin-key-${d}`}>{d}</button>
        ))}
        <button type="button" onClick={onBack} className="h-16 rounded-xl text-white/60 hover:bg-white/[0.08] flex items-center justify-center" aria-label="Voltar" data-testid="pin-back-btn"><ArrowLeft className="w-6 h-6" /></button>
        <button type="button" onClick={() => press("0")} disabled={busy} className="h-16 rounded-xl bg-white/[0.06] hover:bg-white/[0.14] active:scale-95 text-2xl font-semibold text-white transition-all" data-testid="pin-key-0">0</button>
        <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} className="h-16 rounded-xl text-white/60 hover:bg-white/[0.08] flex items-center justify-center" aria-label="Apagar" data-testid="pin-delete-btn"><Delete className="w-6 h-6" /></button>
      </div>
      {busy && <p className="text-xs text-white/50">A verificar…</p>}
    </div>
  );
};

export default function ProfilePickerPage() {
  const { device, loading, refreshDevice, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [picked, setPicked] = useState(null);
  const [checked, setChecked] = useState(false);
  const from = location.state?.from?.pathname || "/dashboard";

  // Confirma o estado do dispositivo no servidor (o estado em memória pode estar desactualizado logo após o login)
  useEffect(() => {
    let alive = true;
    refreshDevice().then((d) => {
      if (!alive) return;
      if (!d?.unlocked) navigate("/login?classic=1", { replace: true });
      else setChecked(true);
    });
    return () => { alive = false; };
  }, [refreshDevice, navigate]);

  if (loading || !device || !checked || !device.unlocked) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const pick = (p) => {
    if (p.is_current && user?.user_id === p.user_id) { navigate(from, { replace: true }); return; }
    if (!p.has_pin) { toast.error(`${p.name} ainda não tem PIN — um administrador define-o em RH › Funcionários`); return; }
    if (p.locked) { toast.error("PIN bloqueado temporariamente por tentativas erradas"); return; }
    setPicked(p);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="profile-picker-page">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {device.company_logo ? <img src={device.company_logo} alt="" className="w-9 h-9 rounded-lg object-contain bg-white/5" /> : <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold">{initials(device.company_name || "L")}</div>}
          <div>
            <p className="font-semibold leading-tight" data-testid="profile-picker-company">{device.company_name || "Empresa"}</p>
            <p className="text-xs text-white/40">Dispositivo desbloqueado por {device.unlocked_by || "administrador"}</p>
          </div>
        </div>
        <Link to="/login?classic=1" className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="classic-login-link"><LogIn className="w-3.5 h-3.5" /> Entrar com email e palavra-passe</Link>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        {picked ? (
          <PinPad profile={picked} onBack={() => setPicked(null)} onDone={() => navigate(from, { replace: true })} />
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-2">Quem está a entrar?</h1>
            <p className="text-white/50 mb-10 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Escolha o seu perfil e introduza o PIN de 4 dígitos</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full max-w-5xl" data-testid="profile-grid">
              {device.profiles.map((p) => <ProfileCard key={p.user_id} p={p} onPick={pick} />)}
            </div>
            {device.profiles.length === 0 && <p className="text-white/50">Sem perfis activos nesta empresa.</p>}
          </>
        )}
      </main>

      <footer className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-white/40 border-t border-white/5">
        <span>PIN protegido: 5 tentativas erradas bloqueiam o perfil durante 5 minutos.</span>
        <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10" onClick={async () => { navigate("/login?classic=1", { replace: true }); await logout(true); }} data-testid="lock-device-btn">
          <ShieldOff className="w-4 h-4 mr-1.5" /> Bloquear dispositivo
        </Button>
      </footer>
    </div>
  );
}
